import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

interface Chunk {
  id: number;
  index: number;
  text: string;
  text_preview?: string;
  context?: string;  // Per risultati ricerca globale
  url?: string;
  source?: string;
  full_length: number;
}

interface IndexInfo {
  id: string;
  name: string;
  type: string;
  chunks_count: number;
  created_at: string;
  status: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  per_page: number;
  has_prev: boolean;
  has_next: boolean;
  start_index: number;  // Changed from start_chunk
  end_index: number;    // Changed from end_chunk
}

const IndexManagementPanel: React.FC = () => {
  const [indices, setIndices] = useState<IndexInfo[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<string | null>(null);
  const [chunks, setChunks] = useState<Chunk[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedChunks, setExpandedChunks] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState('1');
  const [limit] = useState(500); // Chunks per pagina
  const [searchMode, setSearchMode] = useState<'page' | 'global'>('page'); // Modalità ricerca
  const [globalSearchResults, setGlobalSearchResults] = useState<Chunk[]>([]);
  const [globalSearchPagination, setGlobalSearchPagination] = useState<Pagination | null>(null);
  const [globalSearchPage, setGlobalSearchPage] = useState(1);
  const [searching, setSearching] = useState(false);

  // Carica lista indici
  const loadIndices = async () => {
    try {
      const response = await fetch(`${API_URL}/api/flow/kb/list-indices`);
      const data = await response.json();
      setIndices(data.indices || []);
    } catch (error) {
      console.error('❌ Errore caricamento indici:', error);
    }
  };

  useEffect(() => {
    loadIndices();
  }, []);

  // Carica chunks di una pagina specifica
  const loadChunks = async (indexName: string, page: number) => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/indices/${encodeURIComponent(indexName)}/details?page=${page}&limit=${limit}`;
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('📊 Index details loaded:', data);
      
      // Adatta formato da /api/indices/{id}/details a quello atteso
      if (data && data.all_chunks) {
        // Mappa i chunks al formato atteso dal componente
        const mappedChunks = data.all_chunks.map((chunk: any) => ({
          id: chunk.id,
          index: chunk.index || chunk.id,
          text: chunk.content || chunk.text || '',
          source: chunk.source_file || chunk.source || '',
          full_length: chunk.content?.length || chunk.text?.length || 0
        }));
        
        setChunks(mappedChunks);
        setPagination(data.pagination);
        setCurrentPage(page);
        setPageInput(String(page));
        // Reset ricerca quando cambio pagina
        setSearchQuery('');
      }
    } catch (error) {
      console.error('❌ Errore caricamento chunks:', error);
      alert('❌ Errore caricamento chunks - vedi console');
    } finally {
      setLoading(false);
    }
  };

  // Ricerca globale su tutti i chunk
  const performGlobalSearch = async (query: string, page: number = 1) => {
    if (!selectedIndex || query.length < 2) return;
    
    setSearching(true);
    setSearchMode('global');
    try {
      const url = `${API_URL}/api/flow/kb/search-chunks/${encodeURIComponent(selectedIndex)}?q=${encodeURIComponent(query)}&page=${page}&limit=100`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setGlobalSearchResults(data.results || []);
        setGlobalSearchPagination(data.pagination);
        setGlobalSearchPage(page);
      }
    } catch (error) {
      console.error('❌ Errore ricerca globale:', error);
      alert('❌ Errore durante la ricerca');
    } finally {
      setSearching(false);
    }
  };

  // Reset ricerca e torna alla navigazione normale
  const resetSearch = () => {
    setSearchMode('page');
    setSearchQuery('');
    setGlobalSearchResults([]);
    setGlobalSearchPagination(null);
  };

  // Seleziona indice e carica prima pagina
  const handleSelectIndex = (indexName: string) => {
    setSelectedIndex(indexName);
    setExpandedChunks(new Set());
    resetSearch();
    loadChunks(indexName, 1);
  };

  // Navigazione pagine
  const goToPage = (page: number) => {
    if (selectedIndex && pagination) {
      if (page < 1) page = 1;
      if (page > pagination.total_pages) page = pagination.total_pages;
      loadChunks(selectedIndex, page);
    }
  };

  const handlePageInputSubmit = () => {
    const page = parseInt(pageInput);
    if (!isNaN(page) && pagination) {
      goToPage(page);
    }
  };

  // Toggle espansione chunk
  const toggleChunk = (chunkId: number) => {
    const newExpanded = new Set(expandedChunks);
    if (newExpanded.has(chunkId)) {
      newExpanded.delete(chunkId);
    } else {
      newExpanded.add(chunkId);
    }
    setExpandedChunks(newExpanded);
  };

  // Espandi/comprimi tutti
  const toggleAllChunks = () => {
    const currentChunks = searchMode === 'global' ? globalSearchResults : chunks;
    if (expandedChunks.size === currentChunks.length) {
      setExpandedChunks(new Set());
    } else {
      setExpandedChunks(new Set(currentChunks.map(c => c.id)));
    }
  };

  // Filtra chunks in base alla ricerca (SOLO nella pagina corrente)
  const filteredChunks = chunks.filter(chunk => {
    if (!searchQuery || searchMode === 'global') return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      chunk.text.toLowerCase().includes(searchLower) ||
      (chunk.source && chunk.source.toLowerCase().includes(searchLower))
    );
  });

  // Determina quali chunk mostrare
  const displayChunks = searchMode === 'global' ? globalSearchResults : filteredChunks;

  // Copia chunk negli appunti
  const copyChunk = (chunk: Chunk) => {
    const text = `Chunk ${chunk.index}\nSource: ${chunk.source || 'N/A'}\n\n${chunk.text}`;
    navigator.clipboard.writeText(text);
    alert('✅ Chunk copiato negli appunti!');
  };

  // Elimina indice
  const deleteIndex = async (indexName: string) => {
    if (!confirm(`Vuoi eliminare l'indice "${indexName}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/flow/kb/delete-index/${encodeURIComponent(indexName)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        alert('✅ Indice eliminato!');
        if (selectedIndex === indexName) {
          setSelectedIndex(null);
          setChunks([]);
          setPagination(null);
        }
        loadIndices();
      } else {
        const error = await response.json();
        alert(`❌ Errore: ${error.detail}`);
      }
    } catch (error) {
      console.error('❌ Errore eliminazione indice:', error);
      alert('❌ Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <h2 className="text-3xl font-bold text-gray-800">📚 Gestione Indici</h2>
          <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
            {indices.length} indici
          </span>
        </div>
        <button
          onClick={loadIndices}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <span>🔄</span>
          <span>Aggiorna</span>
        </button>
      </div>

      {/* Lista Indici */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto" style={{ maxHeight: '400px' }}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Chunks</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creato</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Azioni</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {indices.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    📊 Nessun indice disponibile
                  </td>
                </tr>
              ) : (
                indices.map((index) => (
                  <tr
                    key={index.id}
                    className={`hover:bg-blue-50 cursor-pointer transition-colors ${selectedIndex === index.name ? 'bg-blue-100' : ''}`}
                    onClick={() => handleSelectIndex(index.name)}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">{index.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        index.type === 'web' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {index.type === 'web' ? '🌐 Web' : '📄 PDF'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-gray-600 font-mono">{index.chunks_count.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {index.created_at && index.created_at !== 'N/A' 
                        ? new Date(typeof index.created_at === 'number' ? index.created_at * 1000 : index.created_at).toLocaleString('it-IT') 
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteIndex(index.name);
                        }}
                        className="text-red-600 hover:text-red-800 font-medium transition-colors"
                        title="Elimina indice"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dettagli Indice e Chunks con Paginazione */}
      {selectedIndex && pagination && (
        <div className="bg-white rounded-lg shadow-lg p-6 space-y-4">
          {/* Header chunk */}
          <div className="flex justify-between items-center border-b pb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-800">🔍 {selectedIndex}</h3>
              <p className="text-sm text-gray-600 mt-1">
                Totale: <strong>{pagination.start_index.toLocaleString()}-{pagination.end_index.toLocaleString()}</strong> di <strong>{(indices.find(i => i.name === selectedIndex)?.chunks_count || 0).toLocaleString()}</strong> chunks
              </p>
            </div>
            <button
              onClick={toggleAllChunks}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              {expandedChunks.size === chunks.length ? '📕 Comprimi Tutti' : '📖 Espandi Tutti'}
            </button>
          </div>

          {/* Controlli Paginazione */}
          <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => goToPage(1)}
                disabled={!pagination.has_prev}
                className={`px-3 py-2 text-sm rounded ${
                  pagination.has_prev
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                ⏮️ Prima
              </button>
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={!pagination.has_prev}
                className={`px-3 py-2 text-sm rounded ${
                  pagination.has_prev
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                ⏪ Prec
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Pagina</span>
              <input
                type="number"
                min="1"
                max={pagination.total_pages}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePageInputSubmit()}
                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">di {pagination.total_pages.toLocaleString()}</span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={!pagination.has_next}
                className={`px-3 py-2 text-sm rounded ${
                  pagination.has_next
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Succ ⏩
              </button>
              <button
                onClick={() => goToPage(pagination.total_pages)}
                disabled={!pagination.has_next}
                className={`px-3 py-2 text-sm rounded ${
                  pagination.has_next
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Ultima ⏭️
              </button>
            </div>
          </div>

          {/* Ricerca */}
          <div className="space-y-3">
            <div className="relative">
              <input
                type="text"
                placeholder={searchMode === 'page' ? "🔍 Cerca in questa pagina..." : "🔍 Cerca in TUTTI i chunk..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchMode === 'global' && searchQuery.length >= 2) {
                    performGlobalSearch(searchQuery, 1);
                  }
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchQuery && (
                <button
                  onClick={resetSearch}
                  className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
                  title="Cancella ricerca"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Bottoni ricerca */}
            <div className="flex items-center space-x-2">
              {searchMode === 'page' && searchQuery.length >= 2 && (
                <button
                  onClick={() => performGlobalSearch(searchQuery, 1)}
                  disabled={searching}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm flex items-center space-x-2 disabled:opacity-50"
                >
                  <span>🔍</span>
                  <span>{searching ? 'Ricerca in corso...' : 'Cerca in TUTTI i chunk'}</span>
                </button>
              )}
              {searchMode === 'global' && (
                <>
                  <button
                    onClick={resetSearch}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm"
                  >
                    ⬅️ Torna alla navigazione normale
                  </button>
                  {globalSearchPagination && (
                    <span className="text-sm text-gray-600">
                      Risultati: <strong>{globalSearchPagination.start_index}-{globalSearchPagination.end_index}</strong> di <strong>{globalSearchPagination.total_pages * globalSearchPagination.per_page}</strong>
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Info ricerca */}
          {searchMode === 'page' && searchQuery && (
            <div className="text-sm text-gray-600">
              🔍 Trovati <strong>{filteredChunks.length}</strong> chunks su {chunks.length} in questa pagina
            </div>
          )}
          
          {searchMode === 'global' && globalSearchPagination && (
            <div className="text-sm text-green-600 bg-green-50 p-3 rounded-lg">
              ✅ Ricerca completata: <strong>{globalSearchPagination.start_index}-{globalSearchPagination.end_index}</strong> di <strong>{globalSearchResults.length > 0 ? (globalSearchPagination.total_pages * globalSearchPagination.per_page) : 0}</strong> risultati trovati
            </div>
          )}

          {/* Paginazione risultati ricerca globale */}
          {searchMode === 'global' && globalSearchPagination && globalSearchPagination.total_pages > 1 && (
            <div className="flex justify-center items-center space-x-2 bg-purple-50 p-3 rounded-lg">
              <button
                onClick={() => performGlobalSearch(searchQuery, globalSearchPage - 1)}
                disabled={!globalSearchPagination.has_prev || searching}
                className={`px-3 py-1 text-sm rounded ${
                  globalSearchPagination.has_prev && !searching
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                ⏪ Prec
              </button>
              <span className="text-sm text-gray-700">
                Pagina <strong>{globalSearchPage}</strong> di <strong>{globalSearchPagination.total_pages}</strong>
              </span>
              <button
                onClick={() => performGlobalSearch(searchQuery, globalSearchPage + 1)}
                disabled={!globalSearchPagination.has_next || searching}
                className={`px-3 py-1 text-sm rounded ${
                  globalSearchPagination.has_next && !searching
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Succ ⏩
              </button>
            </div>
          )}

          {/* Lista Chunks */}
          <div className="space-y-2" style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {loading || searching ? (
              <div className="text-center py-8 text-gray-500">⏳ {searching ? 'Ricerca in corso...' : 'Caricamento chunks...'}</div>
            ) : displayChunks.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchMode === 'global' ? '🔍 Nessun risultato trovato' : searchQuery ? '🔍 Nessun chunk trovato in questa pagina' : '📭 Nessun chunk disponibile'}
              </div>
            ) : (
              displayChunks.map((chunk) => (
                <div
                  key={chunk.id}
                  className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-400 transition-colors"
                >
                  <div
                    className="bg-gray-50 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleChunk(chunk.id)}
                  >
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <span className="text-gray-600 font-medium whitespace-nowrap">#{chunk.index}</span>
                      {chunk.source && (
                        <span className="text-xs text-blue-600 truncate" title={chunk.source}>
                          🔗 {chunk.source}
                        </span>
                      )}
                      <span className="text-xs text-gray-500">({chunk.full_length.toLocaleString()} car)</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyChunk(chunk);
                        }}
                        className="text-blue-600 hover:text-blue-800 px-2 py-1 text-sm"
                        title="Copia chunk"
                      >
                        📋
                      </button>
                      <span className="text-gray-400">
                        {expandedChunks.has(chunk.id) ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>
                  {expandedChunks.has(chunk.id) && (
                    <div className="p-4 bg-white border-t border-gray-200">
                      {searchMode === 'global' && chunk.context ? (
                        <>
                          <div className="text-xs text-purple-600 mb-2 font-medium">📍 Contesto (estratto con evidenziazione):</div>
                          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed bg-yellow-50 p-3 rounded">
                            {chunk.context}
                          </pre>
                          {chunk.text.length > (chunk.context?.length || 0) && (
                            <button
                              onClick={() => {
                                // Mostra testo completo
                                alert(chunk.text);
                              }}
                              className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                            >
                              📄 Mostra testo completo ({chunk.full_length} caratteri)
                            </button>
                          )}
                        </>
                      ) : (
                        <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed">
                          {chunk.text}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IndexManagementPanel;
